import type {
  ListIncomingTransfersResponseDtoOutput,
  ListOutgoingTransfersResponseDtoOutput,
} from '@peanuds/shared';
import type {
  GenericTransfer,
  GenericTransferResourceDraft,
  GenericTransferType,
  IncomingTransfer,
  OutgoingTransfer,
  TransactionDirection,
} from './transactions.types';

type IncomingTransferDto = NonNullable<ListIncomingTransfersResponseDtoOutput['data']>[number];
type OutgoingTransferDto = NonNullable<ListOutgoingTransfersResponseDtoOutput['data']>[number];

const toString = (value: unknown) => {
  return typeof value === 'string' ? value : '';
};

const genericTransferDirectionByType = {
  'balance-deposit': 'in',
  'balance-change': 'in',
  'balance-withdrawal': 'out',
  'subscription-fee': 'out',
  'returned-incoming': 'out',
  'returned-incoming-reverse': 'in',
  'returned-outgoing': 'out',
} satisfies Record<GenericTransferType, TransactionDirection>;

const genericTransferTransactionTypeByDirection = {
  in: 'INCOMING_TRANSFER',
  out: 'OUTGOING_TRANSFER',
} satisfies Record<TransactionDirection, GenericTransfer['type']>;

const genericTransferDirectionByAttribute = {
  INBOUND: 'in',
  OUTBOUND: 'out',
} satisfies Record<'INBOUND' | 'OUTBOUND', TransactionDirection>;

const getDateOnly = (value: string | undefined): string => {
  return value?.slice(0, 10) ?? '';
};

const isGenericTransferType = (type: string): type is GenericTransferType => {
  return type in genericTransferDirectionByType;
};

const isGenericTransferDirectionAttribute = (
  direction: string,
): direction is keyof typeof genericTransferDirectionByAttribute => {
  return direction in genericTransferDirectionByAttribute;
};

const getGenericTransferDirection = (
  transfer: GenericTransferResourceDraft,
): TransactionDirection => {
  if (transfer.type !== undefined && isGenericTransferType(transfer.type)) {
    return genericTransferDirectionByType[transfer.type];
  }

  const attributeDirection = transfer.attributes?.direction;

  if (typeof attributeDirection !== 'string') {
    return 'in';
  }

  return isGenericTransferDirectionAttribute(attributeDirection)
    ? genericTransferDirectionByAttribute[attributeDirection]
    : 'in';
};

export const mapIncomingTransfers = (
  incomingTransfers: IncomingTransferDto[],
): IncomingTransfer[] => {
  return incomingTransfers.map((transfer, index) => {
    const fallbackId = transfer.transactionNumber ?? `in-${index}`;

    return {
      ...transfer,
      id: toString(transfer.id) || fallbackId,
      direction: 'in',
      type: 'INCOMING_TRANSFER',
      transactionSource: 'INCOMING_TRANSFER',
      status: transfer.status,
      amount: transfer.amount,
      currency: transfer.currency ?? 'EUR',
      transferDate: transfer.transferDate ?? '',
      senderName: transfer.senderName ?? '',
      senderInformation: transfer.senderInformation ?? '',
      transactionNumber: transfer.transactionNumber ?? '',
    };
  });
};

export const mapOutgoingTransfers = (
  outgoingTransfers: OutgoingTransferDto[],
): OutgoingTransfer[] => {
  return outgoingTransfers.map((transfer, index) => {
    return {
      ...transfer,
      id: toString(transfer.id) || `out-${index}`,
      direction: 'out',
      type: 'OUTGOING_TRANSFER',
      transactionSource: 'OUTGOING_TRANSFER',
      status: transfer.status,
      amount: transfer.amount,
      currency: transfer.currency ?? 'EUR',
      transferDate: transfer.transferDate ?? '',
      transactionNumber: transfer.transactionNumber ?? '',
    };
  });
};

export const mapGenericTransfers = (
  genericTransfers: GenericTransferResourceDraft[],
): GenericTransfer[] => {
  return genericTransfers.map((transfer) => {
    const direction = getGenericTransferDirection(transfer);

    return {
      ...transfer,
      direction,
      type: genericTransferTransactionTypeByDirection[direction],
      transactionSource: 'GENERIC_TRANSFER',
      genericTransferType: transfer.type ?? '',
      status: transfer.status,
      amount: transfer.amount,
      transferDate: getDateOnly(transfer.creationDateTime),
    };
  });
};
